import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor, Share } from 'lucide-react';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay if not dismissed before
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 
                 bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl shadow-2xl 
                 border border-violet-400/30 overflow-hidden animate-slide-up"
      data-testid="pwa-install-prompt"
    >
      <div className="p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
          data-testid="pwa-dismiss-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <img src="/icons/icon.svg" alt="Viral Clipes" className="w-10 h-10" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-lg">
              Instalar Viral Clipes
            </h3>
            <p className="text-white/80 text-sm mt-1">
              Acesse rapidamente no seu dispositivo
            </p>
          </div>
        </div>

        {isIOS ? (
          <div className="mt-4 bg-white/10 rounded-xl p-3">
            <p className="text-white/90 text-sm flex items-center gap-2">
              <Share className="w-4 h-4" />
              Toque em <span className="font-medium">Compartilhar</span> e depois em <span className="font-medium">&quot;Adicionar à Tela Inicial&quot;</span>
            </p>
          </div>
        ) : (
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleInstall}
              className="flex-1 bg-white text-violet-600 font-semibold py-2.5 px-4 rounded-xl
                       hover:bg-violet-50 transition-colors flex items-center justify-center gap-2"
              data-testid="pwa-install-btn"
            >
              <Download className="w-4 h-4" />
              Instalar App
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 text-white/80 hover:text-white transition-colors"
              data-testid="pwa-later-btn"
            >
              Depois
            </button>
          </div>
        )}

        <div className="mt-3 flex items-center justify-center gap-4 text-white/60 text-xs">
          <span className="flex items-center gap-1">
            <Smartphone className="w-3 h-3" /> iOS & Android
          </span>
          <span className="flex items-center gap-1">
            <Monitor className="w-3 h-3" /> Windows & Mac
          </span>
        </div>
      </div>
    </div>
  );
}
